// Pure helpers for the public booking page and the settings preview.

export const CIVIX_PURPLE = '#7C3AED';
const LEGACY_SLOT_COLORS = ['#2196f3', '#3b82f6'];

const LEGACY_LOOK = { backgroundColor: '#ffffff', fontColor: '#000000', slotColor: '#2196f3' };
const CIVIX_LIGHT = { backgroundColor: '#FFFFFF', fontColor: '#0F172A', slotColor: '#7C3AED', slotFontColor: '#FFFFFF', slotBorderColor: '#6D28D9', slotAppearance: 'filled' };
const CIVIX_DARK = { backgroundColor: '#140B22', fontColor: '#F8FAFC', slotColor: '#7C3AED', slotFontColor: '#FFFFFF', slotBorderColor: '#8B5CF6', slotAppearance: 'filled' };

/**
 * Booking-page colours. An agent who never customised the look (still on the old
 * white/black/blue defaults) gets the CIVIX purple theme; any custom colours are kept.
 * @param {object} [appearance]
 * @param {{dark?: boolean}} [opts] - dark = rendering inside the dark CRM shell (settings preview).
 * @returns {{backgroundColor: string, fontColor: string, slotColor: string, slotFontColor: string, slotBorderColor: string, slotAppearance: string}}
 */
export function resolveLook(appearance, { dark = false } = {}) {
  const a = appearance || {};
  const lc = (v) => String(v || '').toLowerCase();
  const untouched = (!a.slotColor || lc(a.slotColor) === LEGACY_LOOK.slotColor || lc(a.slotColor) === '#3b82f6')
    && (!a.backgroundColor || lc(a.backgroundColor) === LEGACY_LOOK.backgroundColor)
    && (!a.fontColor || lc(a.fontColor) === LEGACY_LOOK.fontColor);
  return untouched ? { ...(dark ? CIVIX_DARK : CIVIX_LIGHT), slotAppearance: a.slotAppearance || 'filled' } : { ...CIVIX_LIGHT, ...a };
}

/** Weekday keys, 0=Sunday … 6=Saturday (same mapping as the API). */
export const WEEKDAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

/**
 * Agent-chosen slot colour, but the old default blue is treated as "unset" so the
 * page uses the CIVIX purple.
 * @param {string} [slotColor]
 * @returns {string}
 */
export function brandColor(slotColor) {
  const c = String(slotColor || '').trim();
  return c && !LEGACY_SLOT_COLORS.includes(c.toLowerCase()) ? c : CIVIX_PURPLE;
}

/**
 * Calendar-day key (YYYY-MM-DD) of an instant in `timeZone` (viewer-local when omitted).
 * Grouping by UTC date used to push evening slots onto the next day.
 * @param {string|Date} iso
 * @param {string} [timeZone]
 * @returns {string}
 */
export function dayKeyIn(iso, timeZone) {
  return new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(iso));
}

/**
 * @param {string[]} slots - ISO start instants.
 * @param {string} [timeZone]
 * @returns {Array<{key: string, slots: string[]}>} ascending days, each with ascending slots.
 */
export function groupSlotsByDay(slots, timeZone) {
  const map = new Map();
  [...slots].sort().forEach((s) => {
    const k = dayKeyIn(s, timeZone);
    if (!map.has(k)) map.set(k, []);
    map.get(k).push(s);
  });
  return [...map.entries()].map(([key, list]) => ({ key, slots: list }));
}

/** @returns {{weekday: string, day: string, month: string}} short labels for a YYYY-MM-DD key. */
export function dayPillLabels(key) {
  const d = new Date(`${key}T12:00:00Z`);
  const f = (o) => new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', ...o }).format(d);
  return { weekday: f({ weekday: 'short' }), day: f({ day: 'numeric' }), month: f({ month: 'short' }) };
}

export const formatSlotTime = (iso, timeZone) => new Intl.DateTimeFormat('en-US', { timeZone, hour: 'numeric', minute: '2-digit' }).format(new Date(iso));

const icsStamp = (d) => new Date(d).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
const icsEscape = (s) => String(s || '').replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\n/g, '\\n');

/**
 * RFC 5545 calendar invite text.
 * @param {{startAt: string, minutes?: number, title: string, description?: string, location?: string, uid?: string}} ev
 * @returns {string}
 */
export function buildIcs({ startAt, minutes = 30, title, description = '', location = '', uid }) {
  const end = new Date(new Date(startAt).getTime() + minutes * 60000);
  return [
    'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//CIVIX//Booking//EN', 'CALSCALE:GREGORIAN', 'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${uid || `${icsStamp(startAt)}-${Math.random().toString(36).slice(2, 8)}@civixcrm.ai`}`,
    `DTSTAMP:${icsStamp(new Date())}`,
    `DTSTART:${icsStamp(startAt)}`,
    `DTEND:${icsStamp(end)}`,
    `SUMMARY:${icsEscape(title)}`,
    `DESCRIPTION:${icsEscape(description)}`,
    `LOCATION:${icsEscape(location)}`,
    'END:VEVENT', 'END:VCALENDAR',
  ].join('\r\n');
}

/** Trigger a browser download of an .ics file. */
export function downloadIcs(ics, filename = 'appointment.ics') {
  const url = URL.createObjectURL(new Blob([ics], { type: 'text/calendar;charset=utf-8' }));
  const a = Object.assign(document.createElement('a'), { href: url, download: filename });
  a.click();
  URL.revokeObjectURL(url);
}
